// TASARIM MOTORU — urun baski sablonu uzerinde katmanli tasarim editoru.
//
// Sablon (packages/shared print-templates) tuvalin olcusunu, kesim cizgisini,
// guvenli bolgeyi ve deligi verir. Katmanlar (yazi + gorsel) tuvale ORANLA
// saklanir; boylece daire <-> kalp gecisinde ayni yerlesim tasinir. Kilavuzlar
// disa aktarilan PNG'ye girmez. Kaydet: her varyant ayri PNG olarak Tasarimlar
// havuzuna duser (Design.metadata.engine = belge -> sonra yeniden acilir).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Stage, Layer, Rect, Shape, Text as KText, Image as KImage, Transformer } from "react-konva";
import Konva from "konva";
import useImage from "use-image";
import {
  Undo2, Redo2, Minus, Plus, Type, ImagePlus, Trash2, Copy, ChevronUp, ChevronDown, Save, Download, Loader2,
  Bold, Italic, AlignLeft, AlignCenter, AlignRight, CaseUpper, Tag, Library, Check, Shuffle, Sparkles, Layers,
} from "lucide-react";
import {
  PRINT_TEMPLATES, printTemplateByKey, printGeometry,
  ENGINE_NICHES, ENGINE_STYLES, ENGINE_LAYOUTS, engineStyle, engineNiche, randomBranch, buildFromBranch,
  type EngineDoc, type EngineLayer, type EngineTextLayer, type EngineImageLayer, type PrintVariant,
  type StyleBranch,
} from "@erc/shared";
import { apiClient } from "../lib/api";
import { authHeaders, getBackendUrl } from "../lib/http";
import { FontLibraryModal } from "../components/design/FontLibraryModal";

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${getBackendUrl()}${u}`);
const uid = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

interface CatalogFont { id?: string; family: string; style?: string; source?: "user" | "google"; url?: string }
interface Asset { id: string; kind: string; url: string; width?: number | null; height?: number | null; niche?: string | null; style?: string | null }

async function api<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || j.message || `İstek başarısız (${r.status})`);
  return j.data as T;
}

/** Font katalogu (sablon editoruyle ayni kaynak) + tarayiciya yukleme */
function useFonts(): [CatalogFont[], () => void] {
  const [fonts, setFonts] = useState<CatalogFont[]>([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    void api<CatalogFont[]>("/api/analyses/customization/fonts").then((f) => {
      setFonts(f);
      if (!document.getElementById("erc-template-fonts")) {
        const link = document.createElement("link");
        link.id = "erc-template-fonts";
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?${f.filter((x) => x.source !== "user").map((x) => `family=${x.family.replace(/ /g, "+")}:ital,wght@0,400;0,700;1,400`).join("&")}&display=swap`;
        document.head.appendChild(link);
      }
      let style = document.getElementById("erc-user-fonts") as HTMLStyleElement | null;
      if (!style) { style = document.createElement("style"); style.id = "erc-user-fonts"; document.head.appendChild(style); }
      style.textContent = f.filter((x) => x.source === "user" && x.url)
        .map((x) => `@font-face{font-family:'${x.family.replace(/'/g, "")}';src:url('${abs(x.url)}');font-display:swap}`).join("\n");
    }).catch(() => {});
  }, [tick]);
  return [fonts, () => setTick((t) => t + 1)];
}

function newDoc(templateKey = "ornament"): EngineDoc {
  const t = printTemplateByKey(templateKey) ?? PRINT_TEMPLATES[0];
  return { version: 1, templateKey: t.key, activeVariant: t.variants[0].id, background: "#ffffff", variants: { [t.variants[0].id]: [] } };
}

function textFontStyle(l: EngineTextLayer) {
  return `${l.italic ? "italic " : ""}${l.bold ? "bold" : "normal"}`.trim();
}

// ---------- Tuval dugumleri ----------

function ImageNode({ layer, W, H, onSelect, onChange, nodeRef }: {
  layer: EngineImageLayer; W: number; H: number; onSelect: () => void;
  onChange: (p: Partial<EngineImageLayer>) => void; nodeRef: (n: Konva.Node | null) => void;
}) {
  const [img] = useImage(abs(layer.url), "anonymous");
  return (
    <KImage
      ref={nodeRef}
      image={img}
      x={layer.x * W} y={layer.y * H} width={layer.w * W} height={layer.h * H}
      rotation={layer.rotation} opacity={layer.opacity ?? 1}
      draggable onMouseDown={onSelect} onTap={onSelect}
      onDragEnd={(e) => onChange({ x: e.target.x() / W, y: e.target.y() / H })}
      onTransformEnd={(e) => {
        const n = e.target;
        const w = Math.max(5, n.width() * n.scaleX()), h = Math.max(5, n.height() * n.scaleY());
        n.scaleX(1); n.scaleY(1);
        onChange({ x: n.x() / W, y: n.y() / H, w: w / W, h: h / H, rotation: n.rotation() });
      }}
    />
  );
}

function TextNode({ layer, W, H, onSelect, onChange, nodeRef, fontTick }: {
  layer: EngineTextLayer; W: number; H: number; onSelect: () => void;
  onChange: (p: Partial<EngineTextLayer>) => void; nodeRef: (n: Konva.Node | null) => void; fontTick: number;
}) {
  const size = layer.fontSize * H;
  return (
    <KText
      key={`${layer.id}-${fontTick}`}
      ref={nodeRef}
      text={layer.uppercase ? layer.text.toLocaleUpperCase("en-US") : layer.text}
      x={layer.x * W} y={layer.y * H} width={layer.w * W}
      rotation={layer.rotation}
      fontFamily={layer.fontFamily} fontSize={size} fontStyle={textFontStyle(layer)}
      fill={layer.color} align={layer.align}
      letterSpacing={layer.letterSpacing * size} lineHeight={layer.lineHeight}
      opacity={layer.opacity ?? 1}
      draggable onMouseDown={onSelect} onTap={onSelect}
      onDragEnd={(e) => onChange({ x: e.target.x() / W, y: e.target.y() / H })}
      onTransformEnd={(e) => {
        // Kose tutamaci: yazi boyu + kutu genisligi birlikte olceklenir
        const n = e.target as Konva.Text;
        const sx = n.scaleX(), sy = n.scaleY();
        n.scaleX(1); n.scaleY(1);
        onChange({
          x: n.x() / W, y: n.y() / H, rotation: n.rotation(),
          w: Math.max(10, n.width() * sx) / W,
          fontSize: Math.max(0.005, layer.fontSize * sy),
        });
      }}
    />
  );
}

// ---------- Sayfa ----------

export default function DesignEnginePage() {
  const [params] = useSearchParams();
  const [fonts, reloadFonts] = useFonts();
  const [fontsOpen, setFontsOpen] = useState(false);
  const [doc, setDoc] = useState<EngineDoc>(() => newDoc());
  const past = useRef<EngineDoc[]>([]);
  const future = useRef<EngineDoc[]>([]);
  const [, force] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [fontTick, setFontTick] = useState(0);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [nicheKey, setNicheKey] = useState("engaged");
  // Illustrasyon modeli: secim tarayicida hatirlanir (varsayilan GPT Image 2)
  const [imgModels, setImgModels] = useState<Array<{ id: string; name: string; price?: string }>>([]);
  const [imgModel, setImgModel] = useState<string>(() => {
    try { return localStorage.getItem("engine-image-model") || "openai/gpt-image-2"; } catch { return "openai/gpt-image-2"; }
  });
  useEffect(() => { try { localStorage.setItem("engine-image-model", imgModel); } catch { /* yoksay */ } }, [imgModel]);
  useEffect(() => { void api<Array<{ id: string; name: string; price?: string }>>("/api/analyses/design/models").then(setImgModels).catch(() => {}); }, []);
  const fileRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const guidesRef = useRef<Konva.Layer>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const nodes = useRef<Record<string, Konva.Node | null>>({});
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 800, h: 700 });

  const template = printTemplateByKey(doc.templateKey) ?? PRINT_TEMPLATES[0];
  const variant: PrintVariant = template.variants.find((v) => v.id === doc.activeVariant) ?? template.variants[0];
  const geo = useMemo(() => printGeometry(template, variant), [template, variant]);
  const layers = doc.variants[variant.id] ?? [];
  const selected = layers.find((l) => l.id === selectedId) ?? null;

  // Tuvalin sigdigi olcek (+ yakinlastirma)
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fit = Math.min((box.w - 48) / geo.W, (box.h - 48) / geo.H);
  const scale = Math.max(0.02, fit * zoom);

  // ---- Belge degisikligi + geri al/ileri al
  const commit = useCallback((fn: (d: EngineDoc) => EngineDoc) => {
    setDoc((d) => {
      past.current = [...past.current.slice(-99), d];
      future.current = [];
      return fn(d);
    });
  }, []);
  const undo = () => { const p = past.current.pop(); if (!p) return; future.current.push(doc); setDoc(p); force((x) => x + 1); };
  const redo = () => { const f = future.current.pop(); if (!f) return; past.current.push(doc); setDoc(f); force((x) => x + 1); };

  const setLayers = (fn: (ls: EngineLayer[]) => EngineLayer[]) =>
    commit((d) => ({ ...d, variants: { ...d.variants, [variant.id]: fn(d.variants[variant.id] ?? []) } }));
  const patchLayer = (id: string, p: Partial<EngineLayer>) =>
    setLayers((ls) => ls.map((l) => (l.id === id ? ({ ...l, ...p } as EngineLayer) : l)));

  // ---- Font yuklenince yaziyi yeniden olc (Konva olcumu font gelmeden yapar)
  useEffect(() => {
    const fams = [...new Set(layers.filter((l): l is EngineTextLayer => l.type === "text").map((l) => l.fontFamily))];
    if (fams.length === 0) return;
    void Promise.all(fams.map((f) => document.fonts.load(`32px "${f}"`).catch(() => null))).then(() => setFontTick((t) => t + 1));
  }, [layers.map((l) => (l.type === "text" ? l.fontFamily : "")).join("|"), fonts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Varlik kutuphanesi (yuklemeler)
  const loadAssets = useCallback(() => {
    void api<Asset[]>("/api/engine/assets").then(setAssets).catch(() => {});
  }, []);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  // ---- Kayitli tasarimi ac (?design=ID)
  useEffect(() => {
    const id = params.get("design");
    if (!id) return;
    void apiClient.getDesign(id).then((d) => {
      const eng = (d.metadata as { engine?: EngineDoc } | null)?.engine;
      if (eng?.version === 1) { setDoc(eng); setName(d.name.replace(/ \((Daire|Kalp|.+)\)$/, "")); }
    }).catch(() => {});
  }, [params]);

  // ---- Secim -> Transformer
  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const n = selectedId ? nodes.current[selectedId] : null;
    tr.nodes(n ? [n] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, layers, fontTick]);

  // ---- Klavye: sil, geri al/ileri al
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        setLayers((ls) => ls.filter((l) => l.id !== selectedId));
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ---- Stil matrisi
  const branch = doc.style ?? null;
  const illusFor = (niche: string, style: string) =>
    assets.filter((a) => a.kind === "illustration" && a.niche === niche && a.style === style);

  /** Dali uygula: sablonun TUM varyantlari ayni anda kurulur (daire + kalp) */
  function applyBranch(b: StyleBranch, retitle = false, fresh = false) {
    const style = engineStyle(b.style);
    const pal = style?.palettes.find((p) => p.key === b.palette);
    setSelectedId(null);
    commit((d) => {
      const variants: Record<string, EngineLayer[]> = {};
      for (const v of template.variants) variants[v.id] = buildFromBranch(b, template, v, fresh ? [] : d.variants[v.id] ?? d.variants[d.activeVariant] ?? [], retitle);
      return { ...d, style: b, background: pal?.bg ?? d.background, variants };
    });
  }

  async function generateIllustration(b: StyleBranch): Promise<Asset | null> {
    setGenerating(true);
    setNotice("İllüstrasyon üretiliyor (AI, 10-30 sn)... Kütüphaneye kaydedilecek.");
    try {
      const a = await api<Asset>("/api/engine/illustrations", "POST", { niche: b.niche, style: b.style, palette: b.palette, model: imgModel });
      setAssets((prev) => [a, ...prev]);
      setNotice("İllüstrasyon kütüphaneye eklendi.");
      return a;
    } catch (err) {
      setNotice("İllüstrasyon üretilemedi: " + (err instanceof Error ? err.message : String(err)));
      return null;
    } finally {
      setGenerating(false);
    }
  }

  const toIllus = (a: Asset) => ({ id: a.id, url: a.url, w: a.width ?? 1000, h: a.height ?? 1000 });

  /** Stil sec / Beni sasirt: eksenler rastgele; illustrasyon kutuphaneden,
   *  yoksa bir kez AI ile uretilir */
  async function pickStyle(styleKey?: string) {
    // Nis degistiyse eski nisin metinleri tasinmaz
    const fresh = branch?.niche !== nicheKey;
    const b = randomBranch(nicheKey, styleKey ?? branch?.style);
    b.alias = branch?.niche === nicheKey ? branch.alias ?? null : null;
    const st = engineStyle(b.style)!;
    if (st.illustrationPrompt) {
      const lib = illusFor(b.niche, b.style);
      if (lib.length > 0) b.illus = toIllus(lib[Math.floor(Math.random() * lib.length)]);
      else {
        applyBranch(b, false, fresh); // once yazilar gelsin, illustrasyon arkadan
        const a = await generateIllustration(b);
        if (a) applyBranch({ ...b, illus: toIllus(a) });
        return;
      }
    }
    applyBranch(b, false, fresh);
  }

  function setAxis(p: Partial<StyleBranch>) {
    if (!branch) return;
    applyBranch({ ...branch, ...p }, "alias" in p);
  }

  /** Alt nisler: ayni tasarim, baslik + ad degisir; her biri ayri tasarim olarak kaydedilir */
  async function saveAliases() {
    const niche = branch ? engineNiche(branch.niche) : null;
    if (!branch || !niche?.aliases?.length) return;
    setSaving(true);
    try {
      const done: string[] = [];
      for (const al of niche.aliases) {
        const ab: StyleBranch = { ...branch, alias: al.key };
        const variants: Record<string, EngineLayer[]> = {};
        for (const v of template.variants) {
          const cur = doc.variants[v.id] ?? [];
          // Yalnizca baslik metni degisir; diger duzenlemeler aynen kalir
          variants[v.id] = cur.map((l) => (l.type === "text" && l.role === "title" ? { ...l, text: al.title } : l));
        }
        await saveDoc({ ...doc, style: ab, variants }, `${al.title} ${template.label}`);
        done.push(al.label);
      }
      setNotice(`Alt nişler kaydedildi: ${done.join(", ")}`);
    } catch (err) {
      setNotice("Kaydetme hatası: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  }

  // ---- Sablon / varyant
  function chooseTemplate(key: string) {
    const t = printTemplateByKey(key);
    if (!t) return;
    setSelectedId(null);
    commit((d) => {
      // Ayni katmanlar yeni urune tasinir (oranli koordinatlar)
      const cur = d.variants[d.activeVariant] ?? [];
      if (d.style) {
        // Stil dali varsa yeni urunun guvenli kutusuna yeniden yerlesir
        const variants: Record<string, EngineLayer[]> = {};
        for (const v of t.variants) variants[v.id] = buildFromBranch(d.style, t, v, cur);
        return { ...d, templateKey: t.key, activeVariant: t.variants[0].id, variants };
      }
      return { ...d, templateKey: t.key, activeVariant: t.variants[0].id, variants: { [t.variants[0].id]: cur } };
    });
  }
  function chooseVariant(id: string) {
    setSelectedId(null);
    commit((d) => {
      // Ilk geciste mevcut varyantin katmanlari kopyalanir (sonra bagimsiz)
      const has = d.variants[id];
      const copy = (d.variants[d.activeVariant] ?? []).map((l) => ({ ...l, id: uid() }));
      return { ...d, activeVariant: id, variants: { ...d.variants, [id]: has ?? copy } };
    });
  }

  // ---- Katman ekleme
  function addText() {
    const fam = fonts.find((f) => f.family === "Playfair Display")?.family ?? fonts[0]?.family ?? "Georgia";
    const l: EngineTextLayer = {
      id: uid(), type: "text", text: "Metin", x: geo.safe.x / geo.W, y: 0.42, w: geo.safe.w / geo.W, rotation: 0,
      fontFamily: fam, fontSize: 0.1, color: "#1f2937", align: "center", letterSpacing: 0, lineHeight: 1.1,
    };
    setLayers((ls) => [...ls, l]);
    setSelectedId(l.id);
  }
  function addImageFromAsset(a: Asset) {
    const ar = a.width && a.height ? a.width / a.height : 1;
    const maxW = (geo.safe.w / geo.W) * 0.7;
    let w = maxW, h = (w * geo.W) / ar / geo.H;
    if (h > 0.7) { h = 0.7; w = (h * geo.H * ar) / geo.W; }
    const l: EngineImageLayer = { id: uid(), type: "image", url: a.url, x: 0.5 - w / 2, y: 0.5 - h / 2, w, h, rotation: 0 };
    setLayers((ls) => [...ls, l]);
    setSelectedId(l.id);
  }
  async function uploadFile(f: File) {
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(f); });
      const a = await api<Asset>("/api/engine/assets", "POST", { image: dataUrl, kind: "upload" });
      setAssets((prev) => [a, ...prev]);
      addImageFromAsset(a);
    } catch (err) {
      setNotice("Yükleme hatası: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setUploading(false);
    }
  }

  function moveLayer(id: string, dir: -1 | 1) {
    setLayers((ls) => {
      const i = ls.findIndex((l) => l.id === id), j = i + dir;
      if (i < 0 || j < 0 || j >= ls.length) return ls;
      const c = [...ls]; [c[i], c[j]] = [c[j], c[i]]; return c;
    });
  }
  function duplicateLayer(id: string) {
    const l = layers.find((x) => x.id === id);
    if (!l) return;
    const c = { ...l, id: uid(), x: l.x + 0.02, y: l.y + 0.02 } as EngineLayer;
    setLayers((ls) => [...ls, c]);
    setSelectedId(c.id);
  }

  // ---- Disa aktarma: kilavuzlar ve tutamaclar gizlenir, baski cozunurlugu.
  // Gecici bir belge (baska varyant / alt nis) cizdirilip geri alinir.
  async function exportPng(render: EngineDoc): Promise<string | null> {
    const stage = stageRef.current;
    if (!stage) return null;
    const before = doc;
    setDoc(render);
    await new Promise((r) => setTimeout(r, 450)); // gorseller + fontlar
    const g = guidesRef.current, tr = trRef.current;
    g?.hide(); tr?.hide();
    stage.batchDraw();
    const url = stage.toDataURL({ pixelRatio: 1 / scale, mimeType: "image/png" });
    g?.show(); tr?.show();
    setDoc(before);
    return url;
  }

  /** Belgenin dolu varyantlarini PNG olarak Tasarimlar havuzuna kaydeder */
  async function saveDoc(d: EngineDoc, base: string): Promise<string[]> {
    const vids = template.variants.map((v) => v.id).filter((id) => (d.variants[id]?.length ?? 0) > 0);
    const saved: string[] = [];
    for (const vid of vids) {
      const url = await exportPng({ ...d, activeVariant: vid });
      if (!url) continue;
      const blob = await (await fetch(url)).blob();
      const v = template.variants.find((x) => x.id === vid)!;
      const label = template.variants.length > 1 ? `${base} (${v.label})` : base;
      await apiClient.uploadDesign(new File([blob], `${label}.png`, { type: "image/png" }), {
        name: label,
        source: "engine",
        tags: ["engine", template.key, vid, ...(d.style ? [d.style.niche, d.style.style] : [])],
        metadata: { engine: { ...d, activeVariant: vid }, printTemplate: template.key, variant: vid },
      } as never);
      saved.push(v.label);
    }
    return saved;
  }

  async function save() {
    if (!template.variants.some((v) => (doc.variants[v.id]?.length ?? 0) > 0)) { setNotice("Kaydedilecek katman yok."); return; }
    setSaving(true);
    setSelectedId(null);
    try {
      const saved = await saveDoc(doc, name.trim() || `${template.label} tasarim`);
      setNotice(`Tasarımlar havuzuna kaydedildi: ${saved.join(", ")}`);
    } catch (err) {
      setNotice("Kaydetme hatası: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  }

  async function downloadPng() {
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 50));
    const url = await exportPng(doc);
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name.trim() || template.label).replace(/[^\w\- ]+/g, "")}-${variant.id}.png`;
    a.click();
  }

  // ---- Kilavuz cizimi (kesim disi soluk, kesim, guvenli bolge, delik)
  const drawGuides = (ctx: CanvasRenderingContext2D) => {
    const { W, H, trim, safe, hole } = geo;
    const lw = 1 / scale;
    ctx.save();
    // Kesim disi soluk: tum tuval + kesim sekli, evenodd
    const outside = new Path2D();
    outside.rect(0, 0, W, H);
    const cut = new Path2D();
    if (variant.shape === "rect") cut.rect(trim.x, trim.y, trim.w, trim.h);
    else if (variant.shape === "ellipse") cut.ellipse(trim.x + trim.w / 2, trim.y + trim.h / 2, trim.w / 2, trim.h / 2, 0, 0, Math.PI * 2);
    else heartPath(cut, trim.x, trim.y, trim.w, trim.h);
    outside.addPath(cut);
    ctx.fillStyle = "rgba(255,255,255,0.72)";
    ctx.fill(outside, "evenodd");
    ctx.lineWidth = 2 * lw; ctx.strokeStyle = "#111827";
    ctx.stroke(cut);
    // Guvenli bolge (kesikli)
    const sp = new Path2D();
    if (variant.shape === "rect") sp.rect(safe.x, safe.y, safe.w, safe.h);
    else if (variant.shape === "ellipse") sp.ellipse(safe.x + safe.w / 2, safe.y + safe.h / 2, safe.w / 2, safe.h / 2, 0, 0, Math.PI * 2);
    else heartPath(sp, safe.x, safe.y, safe.w, safe.h);
    ctx.setLineDash([8 * lw, 6 * lw]); ctx.lineWidth = 1.5 * lw; ctx.strokeStyle = "#4b5563";
    ctx.stroke(sp);
    if (hole) {
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(hole.x, hole.y, hole.r, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff"; ctx.fill(); ctx.lineWidth = 2 * lw; ctx.strokeStyle = "#111827"; ctx.stroke();
      ctx.setLineDash([6 * lw, 5 * lw]); ctx.lineWidth = 1.5 * lw; ctx.strokeStyle = "#4b5563";
      ctx.beginPath(); ctx.arc(hole.x, hole.y, hole.r * 2.4, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
  };

  const T = selected?.type === "text" ? (selected as EngineTextLayer) : null;
  const I = selected?.type === "image" ? (selected as EngineImageLayer) : null;
  const field = "w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100";
  const tbBtn = "flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30";
  const sectionTitle = "mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[600px] flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Tasarım Motoru</h1>
          <p className="text-xs text-gray-500">Ürünün baskı şablonu üzerinde katmanlı tasarım · kesim, güvenli alan ve delik görünür, PNG'ye girmez</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setFontsOpen(true)} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <Type className="h-4 w-4" /> Fontlarım
          </button>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tasarım adı" className="w-56 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none" />
          <button onClick={() => void downloadPng()} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <Download className="h-4 w-4" /> PNG
          </button>
          <button onClick={() => void save()} disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Tasarımlara kaydet
          </button>
        </div>
      </div>
      {fontsOpen && <FontLibraryModal fonts={fonts} onClose={() => setFontsOpen(false)} onChanged={reloadFonts} />}
      {notice && (
        <div className="flex items-center justify-between border-b border-emerald-100 bg-emerald-50 px-5 py-1.5 text-xs text-emerald-800">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-emerald-600 hover:underline">Kapat</button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* ===== Tuval ===== */}
        <div className="flex min-w-0 flex-1 flex-col bg-gray-50">
          <div className="flex justify-center py-2">
            <div className="flex items-center gap-0.5 rounded-xl border border-gray-200 bg-white px-1.5 py-1 shadow-sm">
              <button className={tbBtn} onClick={undo} disabled={past.current.length === 0} title="Geri al (Ctrl+Z)"><Undo2 className="h-4 w-4" /></button>
              <button className={tbBtn} onClick={redo} disabled={future.current.length === 0} title="İleri al (Ctrl+Y)"><Redo2 className="h-4 w-4" /></button>
              <span className="mx-1 h-5 w-px bg-gray-200" />
              <button className={tbBtn} onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))} title="Uzaklaştır"><Minus className="h-4 w-4" /></button>
              <button onClick={() => setZoom(1)} className="w-12 text-center text-xs tabular-nums text-gray-600" title="Sığdır">{Math.round(zoom * 100)}%</button>
              <button className={tbBtn} onClick={() => setZoom((z) => Math.min(6, z * 1.25))} title="Yakınlaştır"><Plus className="h-4 w-4" /></button>
              <span className="mx-1 h-5 w-px bg-gray-200" />
              <button className={tbBtn} onClick={addText} title="Yazı ekle"><Type className="h-4 w-4" /></button>
              <button className={tbBtn} onClick={() => fileRef.current?.click()} title="Görsel yükle">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadFile(f); e.target.value = ""; }} />
            </div>
          </div>

          <div ref={boxRef} className="relative flex min-h-0 flex-1 overflow-auto">
            <div className="m-auto p-6">
              <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-200" style={{ width: geo.W * scale, height: geo.H * scale }}>
                <Stage ref={stageRef} width={geo.W * scale} height={geo.H * scale} scaleX={scale} scaleY={scale}
                  onMouseDown={(e) => { if (e.target === e.target.getStage() || e.target.name() === "bg") setSelectedId(null); }}>
                  <Layer>
                    <Rect name="bg" x={0} y={0} width={geo.W} height={geo.H} fill={doc.background ?? undefined} />
                    {layers.map((l) => l.type === "text" ? (
                      <TextNode key={l.id} layer={l} W={geo.W} H={geo.H} fontTick={fontTick}
                        nodeRef={(n) => { nodes.current[l.id] = n; }}
                        onSelect={() => setSelectedId(l.id)} onChange={(p) => patchLayer(l.id, p)} />
                    ) : (
                      <ImageNode key={l.id} layer={l} W={geo.W} H={geo.H}
                        nodeRef={(n) => { nodes.current[l.id] = n; }}
                        onSelect={() => setSelectedId(l.id)} onChange={(p) => patchLayer(l.id, p)} />
                    ))}
                    <Transformer ref={trRef} rotateEnabled anchorSize={10} borderStroke="#6366f1" anchorStroke="#6366f1"
                      keepRatio={selected?.type === "image"}
                      enabledAnchors={selected?.type === "text" ? ["top-left", "top-right", "bottom-left", "bottom-right", "middle-left", "middle-right"] : undefined} />
                  </Layer>
                  <Layer ref={guidesRef} listening={false}>
                    <Shape sceneFunc={(context) => drawGuides(context._context as CanvasRenderingContext2D)} />
                  </Layer>
                </Stage>
              </div>
            </div>
          </div>

          {template.variants.length > 1 && (
            <div className="flex justify-center pb-3">
              <div className="flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                {template.variants.map((v) => (
                  <button key={v.id} onClick={() => chooseVariant(v.id)}
                    className={`rounded-lg px-5 py-1.5 text-sm font-medium transition-colors ${v.id === variant.id ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ===== Sag panel ===== */}
        <aside className="w-[360px] shrink-0 space-y-5 overflow-y-auto border-l border-gray-200 bg-white p-4">
          {/* Stil matrisi */}
          <section className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white p-3">
            <h3 className={`${sectionTitle} flex items-center gap-1`}><Sparkles className="h-3 w-3 text-violet-500" /> Hazır stil</h3>
            <select value={nicheKey} onChange={(e) => setNicheKey(e.target.value)} className={`${field} mb-2`}>
              {(["event", "recipient", "evergreen"] as const).map((g) => (
                <optgroup key={g} label={g === "event" ? "Etkinlik" : g === "recipient" ? "Kime" : "Her zaman"}>
                  {ENGINE_NICHES.filter((n) => n.group === g).map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
                </optgroup>
              ))}
            </select>
            <div className="grid grid-cols-4 gap-1.5">
              {ENGINE_STYLES.map((st) => {
                const on = branch?.style === st.key && branch?.niche === nicheKey;
                return (
                  <button key={st.key} onClick={() => void pickStyle(st.key)} disabled={generating}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-1.5 text-[10px] font-medium transition-all disabled:opacity-50 ${on ? "border-violet-400 bg-white shadow-sm text-violet-700" : "border-gray-200 bg-white text-gray-600 hover:border-violet-200"}`}>
                    <span className="h-8 w-full rounded" style={{ background: `linear-gradient(135deg, ${st.swatch}, ${st.palettes[0].title})` }} />
                    {st.label}
                  </button>
                );
              })}
            </div>
            <button onClick={() => void pickStyle()} disabled={generating}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-600 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5" />} Beni şaşırt
            </button>
            <label className="mt-2 flex items-center gap-2 text-[10px] text-gray-600">
              <span className="shrink-0 font-medium">İllüstrasyon modeli</span>
              <select value={imgModel} onChange={(e) => setImgModel(e.target.value)} className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[11px]">
                {!imgModels.some((m) => m.id === imgModel) && <option value={imgModel}>{imgModel}</option>}
                {imgModels.map((m) => <option key={m.id} value={m.id}>{m.name}{m.price ? ` · ${m.price}` : ""}</option>)}
              </select>
            </label>
            <p className="mt-1.5 text-[10px] leading-relaxed text-gray-500">AI değil: yerleşim, renk, font ve kütüphanedeki illüstrasyon rastgele birleşir; bedava ve anında. Kütüphane boşsa ilk illüstrasyon bir kez AI ile üretilir.</p>

            {branch && (() => {
              const st = engineStyle(branch.style)!;
              const nc = engineNiche(branch.niche);
              const lib = illusFor(branch.niche, branch.style);
              return (
                <div className="mt-3 space-y-2.5 border-t border-violet-100 pt-3">
                  {nc?.aliases && nc.aliases.length > 0 && (
                    <div>
                      <span className="text-[10px] font-medium text-gray-500">Alt niş (başlık)</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {[{ key: "", label: nc.copy.title }, ...nc.aliases].map((a) => (
                          <button key={a.key || "base"} onClick={() => setAxis({ alias: a.key || null })}
                            className={`rounded-full border px-2 py-0.5 text-[10px] ${(branch.alias ?? "") === a.key ? "border-violet-400 bg-violet-600 text-white" : "border-gray-200 bg-white text-gray-600"}`}>{a.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-[10px] font-medium text-gray-500">Yerleşim</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {st.layouts.map((k) => (
                        <button key={k} onClick={() => setAxis({ layout: k })}
                          className={`rounded-md border px-2 py-1 text-[10px] ${branch.layout === k ? "border-violet-400 bg-white text-violet-700 shadow-sm" : "border-gray-200 bg-white text-gray-600"}`}>
                          {ENGINE_LAYOUTS.find((l) => l.key === k)?.label ?? k}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-medium text-gray-500">Renkler</span>
                    <div className="mt-1 flex gap-1.5">
                      {st.palettes.map((p) => (
                        <button key={p.key} onClick={() => setAxis({ palette: p.key })} title={p.tone}
                          className={`flex h-7 items-center gap-0.5 rounded-full border px-1.5 ${branch.palette === p.key ? "border-violet-400 ring-2 ring-violet-200" : "border-gray-200"}`} style={{ background: p.bg }}>
                          {[p.title, p.names, p.date].map((c, i) => <span key={i} className="h-3.5 w-3.5 rounded-full border border-white" style={{ background: c }} />)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-medium text-gray-500">Font</span>
                    <div className="mt-1 grid grid-cols-3 gap-1">
                      {st.fonts.map((f) => (
                        <button key={f.key} onClick={() => setAxis({ font: f.key })} title={`${f.display} + ${f.body}`}
                          className={`rounded-md border bg-white px-1 py-1.5 text-center leading-tight ${branch.font === f.key ? "border-violet-400 shadow-sm" : "border-gray-200"}`}>
                          <span className="block truncate text-base" style={{ fontFamily: f.display }}>Abc</span>
                          <span className="block truncate text-[9px] uppercase tracking-wider text-gray-500" style={{ fontFamily: f.body }}>{f.body}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {st.illustrationPrompt && (
                    <div>
                      <span className="text-[10px] font-medium text-gray-500">İllüstrasyon ({lib.length})</span>
                      <div className="mt-1 grid grid-cols-4 gap-1">
                        {lib.map((a) => (
                          <button key={a.id} onClick={() => setAxis({ illus: toIllus(a) })}
                            className={`aspect-square overflow-hidden rounded-md border bg-white ${branch.illus?.id === a.id ? "border-violet-400 ring-2 ring-violet-200" : "border-gray-200"}`}>
                            <img src={abs(a.url)} alt="" className="h-full w-full object-contain" loading="lazy" />
                          </button>
                        ))}
                        <button onClick={() => void generateIllustration(branch).then((a) => { if (a) setAxis({ illus: toIllus(a) }); })} disabled={generating}
                          title="Bu niş + stil için AI ile yeni illüstrasyon üret (kütüphaneye kaydedilir)"
                          className="flex aspect-square flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-violet-300 bg-white text-[9px] text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI ile üret
                        </button>
                      </div>
                    </div>
                  )}
                  {nc?.aliases && nc.aliases.length > 0 && (
                    <button onClick={() => void saveAliases()} disabled={saving}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-violet-200 bg-white py-1.5 text-[11px] font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                      <Layers className="h-3.5 w-3.5" /> Tüm alt nişleri kaydet ({nc.aliases.length})
                    </button>
                  )}
                </div>
              );
            })()}
          </section>

          <section>
            <h3 className={sectionTitle}>Ürün</h3>
            <select value={template.key} onChange={(e) => chooseTemplate(e.target.value)} className={field}>
              {PRINT_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <p className="mt-1.5 text-[11px] text-gray-500">
              {variant.widthIn}×{variant.heightIn} inç · {template.dpi} DPI · {geo.W}×{geo.H} px{template.hint ? ` · ${template.hint}` : ""}
            </p>
            <p className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-gray-400">
              <span>— kesim</span><span>- - güvenli alan</span>{geo.hole && <span>○ delik</span>}
            </p>
          </section>

          {/* Secili katman */}
          {T && (
            <section className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <h3 className={sectionTitle}>Yazı</h3>
              <textarea value={T.text} rows={2} onChange={(e) => patchLayer(T.id, { text: e.target.value })} className={field} />
              <select value={T.fontFamily} onChange={(e) => patchLayer(T.id, { fontFamily: e.target.value })} className={field} style={{ fontFamily: T.fontFamily }}>
                {!fonts.some((f) => f.family === T.fontFamily) && <option value={T.fontFamily}>{T.fontFamily}</option>}
                {fonts.map((f) => <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>{f.family}{f.source === "user" ? " (yüklenen)" : ""}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <input type="color" value={T.color} onChange={(e) => patchLayer(T.id, { color: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-gray-200" />
                {([["bold", Bold], ["italic", Italic], ["uppercase", CaseUpper]] as const).map(([k, Icon]) => (
                  <button key={k} onClick={() => patchLayer(T.id, { [k]: !T[k] } as Partial<EngineTextLayer>)}
                    className={`flex h-8 w-8 items-center justify-center rounded-md border ${T[k] ? "border-indigo-300 bg-white text-indigo-700" : "border-gray-200 text-gray-500"}`}><Icon className="h-3.5 w-3.5" /></button>
                ))}
                <span className="mx-0.5 h-5 w-px bg-gray-200" />
                {([["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]] as const).map(([k, Icon]) => (
                  <button key={k} onClick={() => patchLayer(T.id, { align: k })}
                    className={`flex h-8 w-8 items-center justify-center rounded-md border ${T.align === k ? "border-indigo-300 bg-white text-indigo-700" : "border-gray-200 text-gray-500"}`}><Icon className="h-3.5 w-3.5" /></button>
                ))}
              </div>
              {([["Boyut", "fontSize", 0.02, 0.4, 0.002, (v: number) => `${Math.round(v * geo.H)} px`],
                ["Harf aralığı", "letterSpacing", -0.1, 0.5, 0.005, (v: number) => v.toFixed(2)],
                ["Satır aralığı", "lineHeight", 0.7, 2, 0.05, (v: number) => v.toFixed(2)]] as const).map(([label, key, min, max, step, fmt]) => (
                <label key={key} className="block">
                  <span className="flex justify-between text-[11px] text-gray-600"><span>{label}</span><span className="tabular-nums">{fmt(T[key])}</span></span>
                  <input type="range" min={min} max={max} step={step} value={T[key]} onChange={(e) => patchLayer(T.id, { [key]: Number(e.target.value) } as Partial<EngineTextLayer>)} className="w-full" />
                </label>
              ))}
              <PersonalizeRow value={T.personalize ?? null} onChange={(v) => patchLayer(T.id, { personalize: v })} />
            </section>
          )}
          {I && (
            <section className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <h3 className={sectionTitle}>Görsel</h3>
              <label className="block">
                <span className="flex justify-between text-[11px] text-gray-600"><span>Opaklık</span><span className="tabular-nums">%{Math.round((I.opacity ?? 1) * 100)}</span></span>
                <input type="range" min={0.1} max={1} step={0.05} value={I.opacity ?? 1} onChange={(e) => patchLayer(I.id, { opacity: Number(e.target.value) })} className="w-full" />
              </label>
              <button onClick={() => { const cx = 0.5 - I.w / 2, cy = 0.5 - I.h / 2; patchLayer(I.id, { x: cx, y: cy, rotation: 0 }); }}
                className="w-full rounded-md border border-gray-200 bg-white py-1.5 text-xs text-gray-700 hover:bg-gray-50">Ortala</button>
              <PersonalizeRow value={I.personalize ?? null} onChange={(v) => patchLayer(I.id, { personalize: v })} image />
            </section>
          )}

          {/* Katmanlar */}
          <section>
            <h3 className={sectionTitle}>Katmanlar</h3>
            {layers.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 p-3 text-center text-[11px] text-gray-400">Üstteki araçlarla yazı ya da görsel ekle.</p>
            ) : (
              <div className="space-y-1">
                {[...layers].reverse().map((l) => (
                  <div key={l.id} onClick={() => setSelectedId(l.id)}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 ${l.id === selectedId ? "border-indigo-300 bg-indigo-50/60" : "border-gray-200 hover:border-gray-300"}`}>
                    {l.type === "text" ? <Type className="h-3.5 w-3.5 shrink-0 text-gray-400" /> : <img src={abs(l.url)} alt="" className="h-6 w-6 shrink-0 rounded object-cover" />}
                    <span className="min-w-0 flex-1 truncate text-xs text-gray-700" style={l.type === "text" ? { fontFamily: l.fontFamily } : undefined}>
                      {l.type === "text" ? l.text || "(boş)" : "Görsel"}
                    </span>
                    {l.personalize && <Tag className="h-3 w-3 shrink-0 text-amber-500" />}
                    <span className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => moveLayer(l.id, 1)} title="Öne al" className="rounded p-0.5 text-gray-400 hover:bg-gray-100"><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveLayer(l.id, -1)} title="Arkaya al" className="rounded p-0.5 text-gray-400 hover:bg-gray-100"><ChevronDown className="h-3.5 w-3.5" /></button>
                      <button onClick={() => duplicateLayer(l.id)} title="Çoğalt" className="rounded p-0.5 text-gray-400 hover:bg-gray-100"><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => { setLayers((ls) => ls.filter((x) => x.id !== l.id)); if (selectedId === l.id) setSelectedId(null); }} title="Sil" className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Arka plan */}
          <section>
            <h3 className={sectionTitle}>Arka plan</h3>
            <div className="flex items-center gap-2">
              <input type="color" value={doc.background ?? "#ffffff"} onChange={(e) => commit((d) => ({ ...d, background: e.target.value }))}
                disabled={doc.background === null} className="h-8 w-10 cursor-pointer rounded border border-gray-200 disabled:opacity-40" />
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={doc.background === null} onChange={(e) => commit((d) => ({ ...d, background: e.target.checked ? null : "#ffffff" }))} />
                Saydam
              </label>
            </div>
          </section>

          {/* Yuklemelerim */}
          <section>
            <h3 className={`${sectionTitle} flex items-center gap-1`}><Library className="h-3 w-3" /> Görsellerim</h3>
            {assets.length === 0 ? (
              <p className="text-[11px] text-gray-400">Henüz yükleme yok. Üstteki görsel düğmesiyle ekle.</p>
            ) : (
              <div className="grid grid-cols-4 gap-1.5">
                {assets.slice(0, 40).map((a) => (
                  <button key={a.id} onClick={() => addImageFromAsset(a)} title="Tuvale ekle"
                    className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] hover:border-indigo-300">
                    <img src={abs(a.url)} alt="" className="h-full w-full object-contain" loading="lazy" />
                    <span className="absolute inset-0 hidden items-center justify-center bg-indigo-600/20 group-hover:flex"><Plus className="h-4 w-4 text-indigo-700" /></span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

/** Kisisellestirme etiketi: siparis gelince bu katman alicinin verisiyle degisir */
function PersonalizeRow({ value, onChange, image }: { value: string | null; onChange: (v: string | null) => void; image?: boolean }) {
  const on = value !== null && value !== undefined;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2">
      <label className="flex items-center gap-1.5 text-[11px] font-medium text-amber-900">
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? (image ? "photo" : "names") : null)} />
        <Tag className="h-3 w-3" /> Kişiselleştirilebilir
        {on && <Check className="ml-auto h-3 w-3 text-amber-600" />}
      </label>
      {on && (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value.replace(/\s+/g, "_").toLowerCase())}
          placeholder={image ? "photo" : "names, date..."} className="mt-1.5 w-full rounded border border-amber-200 bg-white px-2 py-1 text-[11px]" />
      )}
    </div>
  );
}

/** Kalp yolu (area-shape.ts ile ayni 100x100 egriler) — Path2D icin */
function heartPath(p: Path2D, x: number, y: number, w: number, h: number) {
  const sx = w / 100, sy = h / 100, X = (n: number) => x + n * sx, Y = (n: number) => y + n * sy;
  p.moveTo(X(50), Y(98));
  p.bezierCurveTo(X(22), Y(78), X(0), Y(58), X(0), Y(30));
  p.bezierCurveTo(X(0), Y(13), X(13), Y(0), X(28), Y(0));
  p.bezierCurveTo(X(39), Y(0), X(46), Y(6), X(50), Y(15));
  p.bezierCurveTo(X(54), Y(6), X(61), Y(0), X(72), Y(0));
  p.bezierCurveTo(X(87), Y(0), X(100), Y(13), X(100), Y(30));
  p.bezierCurveTo(X(100), Y(58), X(78), Y(78), X(50), Y(98));
  p.closePath();
}
